import type { PageConfig } from 'src/libs/PageConfig';

import type { StatusChangeLocation } from '../../graphql/hirer/updateApplicationsStatus';
import type { IJobApplicationSummary } from '../../stores/JobApplicationStore/JobApplicationSummary';
import { type Bucket, BUCKETS } from '../../stores/JobApplicationStore/types';
import type { IStore } from '../../stores/Store';
import captureEvent from '../../utils/captureEvent';

import {
  buildCandidateDataFromSummaryJobApplication,
  buildHirerData,
  buildJobData,
  type FiltersEventCaptureData,
  type HirerData,
  type JobData,
  type SummaryCandidateData,
} from './buildCaptureEventData';

const eventType = 'candidate_status';
export interface StatusChangeContract {
  candidate: StatusUpdateCandidateData[];
  hirer: HirerData;
  job: JobData;
  filters: FiltersEventCaptureData;
}

type CurrentStatus =
  | 'Accept'
  | 'Interview'
  | 'Viewed'
  | 'Offer'
  | 'Rejected'
  | 'Shortlist';

interface StatusUpdateCandidateData extends SummaryCandidateData {
  statusChangeLocation: StatusChangeLocation;
  currentStatus: CurrentStatus;
}

const STATUS_MAPPING: Record<string, BUCKETS> = {
  Viewed: BUCKETS.INBOX,
  Prescreen: BUCKETS.PRESCREEN,
  Interview: BUCKETS.INTERVIEW,
  Offer: BUCKETS.OFFER,
  Accept: BUCKETS.ACCEPT,
  Shortlist: BUCKETS.SHORTLIST,
  Rejected: BUCKETS.NOT_SUITABLE,
};

const getStatusFromBucket = (bucket: Bucket) =>
  Object.keys(STATUS_MAPPING).find(
    (key: string) => STATUS_MAPPING[key] === bucket,
  );

const buildStatusUpdateCaptureEventData = ({
  statusChangeLocation,
  jobApplications,
  destinationBucket,
  store,
  filters,
  addCustomError,
}: {
  statusChangeLocation: StatusChangeLocation;
  jobApplications: IJobApplicationSummary[];
  destinationBucket: Bucket;
  store: IStore;
  filters: FiltersEventCaptureData;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}): StatusChangeContract | undefined => {
  const candidates: StatusUpdateCandidateData[] = [];

  const currentStatus = getStatusFromBucket(destinationBucket);

  const { jobApplicationStore, userStore, jobStore } = store;

  jobApplications.forEach((jobApplication) => {
    const baseCandidateData = buildCandidateDataFromSummaryJobApplication({
      jobApplication,
      jobApplicationStore,
      eventType,
      addCustomError,
    });

    if (baseCandidateData) {
      candidates.push({
        ...baseCandidateData,
        statusChangeLocation,
        currentStatus,
      } as StatusUpdateCandidateData);
    }
  });

  const hirer = buildHirerData({ store, userStore, eventType, addCustomError });
  const job = buildJobData({ eventType, jobStore, addCustomError });

  if (!job || !hirer || candidates.length === 0) {
    // dont send capture event if data not provided.
    return;
  }

  return {
    candidate: candidates,
    hirer,
    job,
    filters,
  };
};

export const sendStatusUpdateCaptureEvent = async ({
  statusChangeLocation,
  destinationBucket,
  jobApplications,
  store,
  filters,
  addCustomError,
}: {
  statusChangeLocation: StatusChangeLocation;
  jobApplications: IJobApplicationSummary[];
  destinationBucket: Bucket;
  store: IStore;
  filters: FiltersEventCaptureData;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}) => {
  const data = buildStatusUpdateCaptureEventData({
    statusChangeLocation,
    jobApplications,
    destinationBucket,
    store,
    filters,
    addCustomError,
  });

  if (!data) {
    // dont send capture event if data not provided.
    return;
  }
  try {
    await captureEvent({
      eventType,
      data,
      addCustomError,
    });
  } catch (error: any) {
    addCustomError({
      error,
      context: {
        activity: 'CAPTURE_EVENT',
        description: 'error attempting: captureEvent',
        eventType,
      },
    });
  }

  return;
};
